import React, { useState, useEffect, useRef } from 'react';
import {
	FileText,
	Map,
	BarChart3,
	FileSpreadsheet,
	LogOut,
	ChevronRight,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { agentsAPI } from '../../services/api';
import { getDisplayName } from '../../utils/displayName';
import { toast } from 'react-toastify';
import AgentBottomNav from './AgentBottomNav';

const AgentDashboard = () => {
	const { user, logout, isTreatment2 } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [bills, setBills] = useState([]);
	const billsFetchedFor = useRef(null);
	const billsFetchInFlight = useRef(false);
	const billsEffectRunFor = useRef(null);
	// Single effect: run once when we have a stable user id. No treatment refresh here to avoid setUser(enriched) re-render loop; AuthContext enriches on load.
	const stableUserId = user?.id != null ? String(user.id) : user?.userId != null ? String(user.userId) : null;
console.log('bills:', bills);

	useEffect(() => {
  if (!stableUserId) return;

  let cancelled = false;

  const fetchBills = async () => {
    try {
      const data = await agentsAPI.getAssignedBills(stableUserId);

      if (!cancelled) {
        setBills(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      if (!cancelled) {
        toast.error('Failed to load bills');
        console.error('Error fetching bills:', error);
      }
    }
  };

  fetchBills();

  return () => {
    cancelled = true;
  };
}, [stableUserId, location.pathname]);

	const pending = bills.filter((b) => {
		const s = String(b.status || '').toLowerCase();
		return !s || s === 'pending' || s === 'overdue';
	}).length;
	const delivered = bills.filter((b) => String(b.status || '').toLowerCase() === 'delivered').length;

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10'>
				<div className='flex justify-between items-center mb-4'>
					<div>
						<h2 className='text-xl text-white font-black uppercase tracking-tight'>
							Dashboard
						</h2>
						<p className='text-xs text-white/60'>
							{user ? (
								<>
									<span className='text-white/40'>Field agent</span>
									{' · '}
									{getDisplayName(user)}
								</>
							) : (
								'—'
							)}
						</p>
					</div>
					<button
						type='button'
						onClick={(e) => {
							e.preventDefault();
							logout();
						}}
						className='glass-card p-3 rounded-xl'>
						<LogOut className='w-5 h-5 text-white' />
					</button>
				</div>
				<div className='grid grid-cols-2 gap-4'>
					<div className='glass-card p-4 text-center rounded-3xl'>
						<p className='text-2xl font-black text-[#FFC107]'>{pending}</p>
						<p className='text-xs text-white/60 uppercase'>Pending</p>
					</div>
					<div className='glass-card p-4 text-center rounded-3xl'>
						<p className='text-2xl font-black text-[#00E676]'>{delivered}</p>
						<p className='text-xs text-white/60 uppercase'>Delivered</p>
					</div>
				</div>
			</header>

			<main className='flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar'>
				<div
					onClick={(e) => {
						e.preventDefault();
						navigate('/agent/deliver-bills');
					}}
					className='glass-card p-6 border-l-4 border-[#FFC107] flex items-center gap-4 active:scale-95 transition-transform cursor-pointer'>
					<div className='w-12 h-12 bg-[#FFC107]/10 rounded-2xl flex items-center justify-center'>
						<FileText className='w-6 h-6 text-[#FFC107]' />
					</div>
					<div className='flex-1'>
						<h3 className='font-black text-lg text-white'>Deliver Bills</h3>
						<p className='text-xs text-white/60'>Bill delivery</p>
					</div>
					<ChevronRight className='text-white/30' />
				</div>

				{isTreatment2 && (
				<div
					onClick={(e) => {
						e.preventDefault();
						navigate('/agent/properties-map');
					}}
					className='glass-card p-6 border-l-4 border-[#00E676] flex items-center gap-4 active:scale-95 transition-transform cursor-pointer'>
					<div className='w-12 h-12 bg-[#00E676]/10 rounded-2xl flex items-center justify-center'>
						<Map className='w-6 h-6 text-[#00E676]' />
					</div>
					<div className='flex-1'>
						<h3 className='font-black text-lg text-white'>Properties Map</h3>
						<p className='text-xs text-white/60'>
							View all assigned properties
						</p>
					</div>
					<ChevronRight className='text-white/30' />
				</div>
				)}

				<div
					onClick={(e) => {
						e.preventDefault();
						navigate('/agent/reports');
					}}
					className='glass-card p-6 border-l-4 border-[#9C27B0] flex items-center gap-4 active:scale-95 transition-transform cursor-pointer'>
					<div className='w-12 h-12 bg-[#9C27B0]/10 rounded-2xl flex items-center justify-center'>
						<FileSpreadsheet className='w-6 h-6 text-[#9C27B0]' />
					</div>
					<div className='flex-1'>
						<h3 className='font-black text-lg text-white'>Reports</h3>
						<p className='text-xs text-white/60'>Payments, deliveries, and summaries for your assignments</p>
					</div>
					<ChevronRight className='text-white/30' />
				</div>

				<div
					onClick={(e) => {
						e.preventDefault();
						navigate('/agent/performance');
					}}
					className='glass-card p-6 border-l-4 border-[#7E57C2] flex items-center gap-4 active:scale-95 transition-transform cursor-pointer'>
					<div className='w-12 h-12 bg-[#7E57C2]/10 rounded-2xl flex items-center justify-center'>
						<BarChart3 className='w-6 h-6 text-[#7E57C2]' />
					</div>
					<div className='flex-1'>
						<h3 className='font-black text-lg text-white'>Performance</h3>
						<p className='text-xs text-white/60'>Efficiency, progress & activity</p>
					</div>
					<ChevronRight className='text-white/30' />
				</div>
			</main>

			<AgentBottomNav active='data-collection' />
		</div>
	);
};

export default AgentDashboard;


